import { useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft,
  Terminal,
  Copy,
  Check,
  Key,
  FileCode,
  Shield,
  ExternalLink,
  Github,
} from "lucide-react";
import FeedbackButton from "../components/FeedbackButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const EXAMPLE_HOST = BACKEND_URL || "https://cveanalytics.in";

const CURL_PUBLIC = `# Public rate limit: 5 scans / minute per IP, 20 / hour
curl -s -X POST "${EXAMPLE_HOST}/api/sbom/scan" \\
  -H "Content-Type: application/json" \\
  --data '{"content":"{\\"name\\":\\"my-app\\",\\"dependencies\\":{\\"lodash\\":\\"4.17.15\\"}}", "name":"my-app"}'`;

const CURL_API_KEY = `# With API key (ask the admin for one). Default limit: 60 req/min
export CVEA_KEY="cvea_your_key_here"

curl -s -X POST "${EXAMPLE_HOST}/api/sbom/scan" \\
  -H "Authorization: Bearer $CVEA_KEY" \\
  -H "Content-Type: application/json" \\
  --data-binary @sbom.json`;

const CURL_MULTIPART = `# Upload the SBOM as a file (great for ci/cd runners)
curl -s -X POST "${EXAMPLE_HOST}/api/sbom/scan" \\
  -H "Authorization: Bearer $CVEA_KEY" \\
  -F "file=@sbom.cdx.json" \\
  -F "name=my-service"`;

const CURL_CYCLONEDX = `# Raw CycloneDX / SPDX body (what most tools produce)
curl -s -X POST "${EXAMPLE_HOST}/api/sbom/scan" \\
  -H "Content-Type: application/vnd.cyclonedx+json" \\
  -H "X-SBOM-Name: my-app" \\
  --data-binary @sbom.cdx.json`;

const CURL_SARIF = `# SARIF output (ingestible by GitHub Code Scanning)
curl -s -X POST "${EXAMPLE_HOST}/api/sbom/scan?format=sarif" \\
  -H "Authorization: Bearer $CVEA_KEY" \\
  -H "Content-Type: application/vnd.cyclonedx+json" \\
  --data-binary @sbom.cdx.json \\
  -o report.sarif`;

const CURL_CSV = `# CSV output (spreadsheet / Jira import)
curl -s -X POST "${EXAMPLE_HOST}/api/sbom/scan?format=csv" \\
  -H "Content-Type: application/json" \\
  --data '{"content": "$(cat requirements.txt | jq -Rs .)"}' \\
  -o findings.csv`;

const CURL_FAIL_ON = `# Pipeline gating: exit non-zero when HIGH+ findings exist
RES=$(curl -s -o /tmp/report.json -w "%{http_code}" \\
  -X POST "${EXAMPLE_HOST}/api/sbom/scan?fail_on=high" \\
  -H "Authorization: Bearer $CVEA_KEY" \\
  -H "Content-Type: application/vnd.cyclonedx+json" \\
  --data-binary @sbom.cdx.json)

if [ "$RES" = "422" ]; then
  echo "::error::High+ vulnerabilities found - failing the build"
  cat /tmp/report.json | jq '.severity_counts'
  exit 1
fi`;

const CURL_FETCH = `# Re-fetch a prior scan (great for multi-step pipelines)
SCAN_ID="4e31e967de9b4109926524536b139500"
curl -s "${EXAMPLE_HOST}/api/sbom/$SCAN_ID?format=sarif" -o report.sarif`;

const GITHUB_ACTION = `# .github/workflows/sbom-scan.yml
name: SBOM Vulnerability Scan
on: [push, pull_request]

jobs:
  scan:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4

      - name: Generate CycloneDX SBOM
        uses: anchore/sbom-action@v0
        with:
          format: cyclonedx-json
          output-file: sbom.cdx.json

      - name: Scan with CVE/Analytics
        id: scan
        env:
          CVEA_KEY: \${{ secrets.CVEA_API_KEY }}
        run: |
          HTTP=$(curl -s -o report.sarif -w "%{http_code}" \\
            -X POST "${EXAMPLE_HOST}/api/sbom/scan?format=sarif&fail_on=high" \\
            -H "Authorization: Bearer $CVEA_KEY" \\
            -H "Content-Type: application/vnd.cyclonedx+json" \\
            --data-binary @sbom.cdx.json)
          echo "http=$HTTP" >> $GITHUB_OUTPUT

      - name: Upload SARIF to GitHub Code Scanning
        if: always()
        uses: github/codeql-action/upload-sarif@v3
        with:
          sarif_file: report.sarif

      - name: Fail on high+ severity
        if: steps.scan.outputs.http == '422'
        run: exit 1`;

const PY_SCRIPT = `# scan.py — one-shot programmatic client
import os, sys, requests

API  = "${EXAMPLE_HOST}/api"
KEY  = os.environ["CVEA_API_KEY"]
SBOM = open("sbom.cdx.json","rb").read()

r = requests.post(
    f"{API}/sbom/scan",
    params={"format": "json", "fail_on": "high"},
    headers={
        "Authorization": f"Bearer {KEY}",
        "Content-Type":  "application/vnd.cyclonedx+json",
    },
    data=SBOM,
    timeout=60,
)

print("status:", r.status_code)
print("findings:", r.json().get("stats", {}))
if r.status_code == 422:
    print("Gate fired. Severity counts:", r.json()["severity_counts"])
    sys.exit(1)`;

const ENDPOINTS = [
  {
    method: "POST",
    path: "/api/sbom/scan",
    desc: "Scan an SBOM/manifest. Accepts JSON, multipart, or raw CycloneDX/SPDX. Query: format (json|sarif|csv|vex), include=vex, fail_on=any|low|medium|high|critical.",
  },
  {
    method: "GET",
    path: "/api/sbom/{scan_id}",
    desc: "Re-fetch a stored scan in any format. Query: format (json|sarif|csv|vex).",
  },
  {
    method: "GET",
    path: "/api/sbom/{scan_id}/export.pdf",
    desc: "Branded PDF report with VEX-style findings table.",
  },
  {
    method: "POST",
    path: "/api/sbom/vex",
    desc: "Build a CycloneDX VEX 1.5 document from an array of triage decisions.",
  },
];

function CodeBlock({ code, label, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        // Fallback for headless/CI browsers where the Clipboard API is blocked.
        const ta = document.createElement("textarea");
        ta.value = code;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Last-ditch fallback: select the <pre> content so the user can ctrl-c.
      try {
        const pre = document.getElementById(`code-${testid}`);
        if (pre) {
          const range = document.createRange();
          range.selectNodeContents(pre);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
          toast.message("Selected · press Ctrl/⌘-C to copy");
        } else {
          toast.error("Copy failed");
        }
      } catch {
        toast.error("Copy failed");
      }
    }
  };
  return (
    <div
      className="relative bg-slate-50 border border-slate-200 rounded-md overflow-hidden"
      data-testid={testid}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-slate-500">
          <Terminal className="h-3 w-3 text-violet-700" />
          {label}
        </div>
        <button
          onClick={onCopy}
          className="flex items-center gap-1 text-[11px] font-mono text-slate-600 hover:text-violet-700 transition-colors"
          data-testid={`${testid}-copy`}
        >
          {copied ? (
            <>
              <Check className="h-3 w-3 text-emerald-700" />
              <span className="text-emerald-700">copied</span>
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              copy
            </>
          )}
        </button>
      </div>
      <pre
        id={`code-${testid}`}
        className="p-4 text-xs text-slate-800 overflow-x-auto whitespace-pre font-mono leading-relaxed"
      >
        {code}
      </pre>
    </div>
  );
}

function MethodChip({ method }) {
  const tone = {
    GET: "border-emerald-200 text-emerald-700 bg-emerald-50",
    POST: "border-violet-200 text-violet-700 bg-violet-50",
    DELETE: "border-red-200 text-red-700 bg-red-50",
  }[method];
  return (
    <span
      className={`font-mono text-[10px] px-1.5 py-0.5 border rounded-sm uppercase tracking-wider ${tone}`}
    >
      {method}
    </span>
  );
}

export default function ApiPage() {
  return (
    <div className="App">
      <Toaster
        theme="light"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            color: "#0f172a",
          },
        }}
      />
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-3 flex items-center gap-6">
          <Link
            to="/"
            data-testid="api-back-link"
            className="flex items-center gap-2 text-slate-700 hover:text-violet-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-display font-bold tracking-tight">
              CVE Analytics
            </span>
          </Link>
          <div className="overline hidden sm:block">API Reference</div>
          <div className="flex-1" />
          <a
            href={`${BACKEND_URL}/api/docs`}
            target="_blank"
            rel="noreferrer"
            data-testid="api-swagger-link"
            className="hidden md:inline-flex items-center gap-1 text-xs font-mono text-slate-600 hover:text-violet-700 transition-colors"
          >
            Swagger
            <ExternalLink className="h-3 w-3" />
          </a>
          <FeedbackButton variant="header" />
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-10 md:py-14 space-y-10">
        <section>
          <div className="overline mb-2 flex items-center gap-2">
            <FileCode className="h-3 w-3 text-violet-700" />
            Programmatic SBOM scanning
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight leading-tight">
            Automate the SBOM scanner.{" "}
            <span className="text-violet-700">curl-friendly.</span>{" "}
            CI-ready.
          </h1>
          <p className="text-slate-600 text-sm md:text-base mt-4 max-w-3xl leading-relaxed">
            The same engine that powers{" "}
            <Link to="/sbom" className="text-violet-700 hover:underline">
              /sbom
            </Link>{" "}
            is a plain HTTP API. Point it at a CycloneDX, SPDX, package.json, or
            requirements.txt and it returns JSON, SARIF, CSV, or a CycloneDX VEX
            document. Perfect for GitHub Actions, nightly jobs, or a tiny Python
            script. Full OpenAPI spec lives at{" "}
            <a
              href={`${BACKEND_URL}/api/docs`}
              target="_blank"
              rel="noreferrer"
              className="text-violet-700 hover:underline inline-flex items-center gap-1"
            >
              /api/docs
              <ExternalLink className="h-3 w-3" />
            </a>
            .
          </p>
        </section>

        <section className="panel p-6 md:p-7" data-testid="api-endpoints">
          <h2 className="font-display font-bold text-lg md:text-xl mb-4">
            Endpoints
          </h2>
          <div className="space-y-3">
            {ENDPOINTS.map((e) => (
              <div
                key={`${e.method}-${e.path}`}
                className="flex flex-col md:flex-row md:items-start gap-3 md:gap-4 bg-slate-50 border border-slate-200 rounded-md p-3"
              >
                <div className="flex items-center gap-2 md:w-28 shrink-0">
                  <MethodChip method={e.method} />
                </div>
                <div className="flex-1 min-w-0">
                  <code className="font-mono text-sm text-violet-700 break-all">
                    {e.path}
                  </code>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                    {e.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="panel p-6 md:p-7" data-testid="api-auth">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="h-4 w-4 text-violet-700" />
            <h2 className="font-display font-bold text-lg md:text-xl">
              Authentication
            </h2>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            The API is public: no key is required and you get{" "}
            <span className="text-slate-900 font-mono">5 scans/min</span> per IP out
            of the box. For CI/CD you'll want a key so a shared pool of runners
            doesn't throttle itself. Ask the admin to create one for you.
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4 flex items-start gap-3">
            <Key className="h-4 w-4 text-violet-700 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="font-mono text-xs text-slate-800 mb-1">
                Authorization: Bearer cvea_&lt;your-key&gt;
              </div>
              <div className="text-[11px] text-slate-500">
                Or send it as{" "}
                <code className="font-mono text-violet-700">X-API-Key</code>.
                Default rate limit for keyed clients: 60 req/min (configurable
                per key).
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-5" data-testid="api-recipes">
          <h2 className="font-display font-bold text-lg md:text-xl">
            Recipes
          </h2>

          <div className="space-y-5">
            <div>
              <h3 className="font-display font-semibold text-sm md:text-base mb-2 text-slate-800">
                1. Minimal public scan
              </h3>
              <CodeBlock
                code={CURL_PUBLIC}
                label="curl · public"
                testid="recipe-public"
              />
            </div>

            <div>
              <h3 className="font-display font-semibold text-sm md:text-base mb-2 text-slate-800">
                2. Authenticated scan with API key
              </h3>
              <CodeBlock
                code={CURL_API_KEY}
                label="curl · api-key"
                testid="recipe-key"
              />
            </div>

            <div>
              <h3 className="font-display font-semibold text-sm md:text-base mb-2 text-slate-800">
                3. File upload (multipart/form-data)
              </h3>
              <CodeBlock
                code={CURL_MULTIPART}
                label="curl · multipart"
                testid="recipe-multipart"
              />
            </div>

            <div>
              <h3 className="font-display font-semibold text-sm md:text-base mb-2 text-slate-800">
                4. Raw CycloneDX body
              </h3>
              <CodeBlock
                code={CURL_CYCLONEDX}
                label="curl · cyclonedx"
                testid="recipe-cyclonedx"
              />
            </div>

            <div>
              <h3 className="font-display font-semibold text-sm md:text-base mb-2 text-slate-800">
                5. SARIF output for GitHub Code Scanning
              </h3>
              <CodeBlock
                code={CURL_SARIF}
                label="curl · sarif"
                testid="recipe-sarif"
              />
            </div>

            <div>
              <h3 className="font-display font-semibold text-sm md:text-base mb-2 text-slate-800">
                6. CSV output for spreadsheets / Jira
              </h3>
              <CodeBlock
                code={CURL_CSV}
                label="curl · csv"
                testid="recipe-csv"
              />
            </div>

            <div>
              <h3 className="font-display font-semibold text-sm md:text-base mb-2 text-slate-800">
                7. Pipeline gating with{" "}
                <code className="font-mono text-violet-700">fail_on</code>
              </h3>
              <p className="text-xs text-slate-500 mb-2">
                The endpoint returns HTTP 422 when findings at or above the
                requested severity exist, so your pipeline can fail without
                extra tooling.
              </p>
              <CodeBlock
                code={CURL_FAIL_ON}
                label="curl · fail_on=high"
                testid="recipe-fail-on"
              />
            </div>

            <div>
              <h3 className="font-display font-semibold text-sm md:text-base mb-2 text-slate-800">
                8. Re-fetch a prior scan
              </h3>
              <CodeBlock
                code={CURL_FETCH}
                label="curl · fetch"
                testid="recipe-fetch"
              />
            </div>

            <div>
              <h3 className="font-display font-semibold text-sm md:text-base mb-2 text-slate-800 flex items-center gap-2">
                <Github className="h-4 w-4" />
                9. GitHub Action — SARIF + Code Scanning
              </h3>
              <CodeBlock
                code={GITHUB_ACTION}
                label="yaml · github workflow"
                testid="recipe-gha"
              />
            </div>

            <div>
              <h3 className="font-display font-semibold text-sm md:text-base mb-2 text-slate-800">
                10. Python one-liner
              </h3>
              <CodeBlock
                code={PY_SCRIPT}
                label="python · requests"
                testid="recipe-python"
              />
            </div>
          </div>
        </section>

        <section className="panel p-6 md:p-7 text-sm text-slate-600">
          <h2 className="font-display font-bold text-base md:text-lg mb-2 text-slate-900">
            Severity derivation
          </h2>
          <p className="leading-relaxed">
            We parse the OSV-provided CVSS v3.x vector string on every finding
            and map the computed base score to CRITICAL / HIGH / MEDIUM / LOW
            using FIRST.org's standard buckets. When a finding has no vector,
            severity falls back to UNKNOWN and does{" "}
            <span className="text-slate-900">not</span> trip{" "}
            <code className="font-mono text-violet-700">fail_on</code> gates (set{" "}
            <code className="font-mono text-violet-700">fail_on=any</code> if you
            want zero-tolerance builds).
          </p>
        </section>
      </main>
    </div>
  );
}
